import { PageShell } from '../../layout/Shell';
import { getReplay, listReplays } from '../../shared/api/client';
import { replayFromApi } from '../../shared/api/transform';
import { SpeedChart, SyncContext } from '../../shared/design/Charts';
import { Icon } from '../../shared/design/Icons';
import { Minimap } from '../../shared/design/Minimap';
import {
  Button,
  Chip,
  EmptyState,
  ParseProgress,
  ReplayCard,
  TierBadge,
} from '../../shared/design/Primitives';
import { useAsync } from '../../shared/hooks/useAsync';
import { Replay } from '../../shared/model/Replay';

export function FoundationsPage() {
  // Pull one real replay so the data-bound demo components (chart, card,
  // minimap) render against actual numbers. Empty state if the DB is dry.
  const state = useAsync<Replay | null>(async () => {
    const summaries = await listReplays(1);
    if (summaries.length === 0) return null;
    return replayFromApi(await getReplay(summaries[0].ghostUid));
  }, []);

  return (
    <PageShell active="home">
      <div className="tm" style={{ padding: 40, maxWidth: 1500, margin: '0 auto' }}>
        <FoundationsFrame />
        <div style={{ height: 60 }} />
        <ComponentsFrame replay={state.data ?? null} loading={state.loading} />
        <div style={{ height: 80 }} />
      </div>
    </PageShell>
  );
}

function FoundationsFrame() {
  return (
    <div className="tm" style={{ background: 'var(--bg-page)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 8 }}>
        <h1 className="h1" style={{ color: 'var(--fg-1)' }}>
          Design tokens
        </h1>
        <span className="eyebrow">Foundations · v0.4</span>
      </div>
      <div className="p" style={{ marginBottom: 32, maxWidth: 720 }}>
        Dark, motorsport-telemetry palette. Sans for UI, mono for every numeric reading. Speed ramp grades the racing line; tier ramp
        grades replays.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
        <div>
          <div className="eyebrow" style={{ marginBottom: 12 }}>Surfaces</div>
          <div
            style={{
              display: 'flex',
              gap: 0,
              borderRadius: 8,
              overflow: 'hidden',
              border: '1px solid var(--border)',
              marginBottom: 28,
            }}
          >
            {[
              { name: 'header', color: '#080808', token: '--bg-header' },
              { name: 'page', color: '#0d0d0d', token: '--bg-page' },
              { name: 'rail', color: '#0a0a0a', token: '--bg-rail' },
              { name: 'surface', color: '#161616', token: '--bg-surface' },
              { name: 'surface-2', color: '#1c1c1c', token: '--bg-surface-2' },
              { name: 'surface-3', color: '#232323', token: '--bg-surface-3' },
            ].map((s) => (
              <div
                key={s.name}
                style={{
                  flex: 1,
                  height: 80,
                  background: s.color,
                  padding: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                }}
              >
                <div style={{ fontSize: 10, color: 'var(--fg-3)', fontFamily: 'var(--font-mono)' }}>{s.token}</div>
                <div style={{ fontSize: 11, color: 'var(--fg-1)' }}>{s.color}</div>
              </div>
            ))}
          </div>

          <div className="eyebrow" style={{ marginBottom: 12 }}>Accents & status</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 28 }}>
            {[
              { name: 'accent', color: '#0099ff', label: 'Primary / TM blue' },
              { name: 'ok', color: '#00ff88', label: 'Clean / S' },
              { name: 'warn', color: '#ffb84d', label: 'Suspicious' },
              { name: 'bad', color: '#ff3355', label: 'Cheated' },
            ].map((s) => (
              <div
                key={s.name}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  background: 'var(--bg-surface)',
                  overflow: 'hidden',
                }}
              >
                <div style={{ height: 48, background: s.color }} />
                <div style={{ padding: 10 }}>
                  <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: s.color, fontWeight: 600 }}>{s.color}</div>
                  <div style={{ fontSize: 10, color: 'var(--fg-3)', marginTop: 2 }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="eyebrow" style={{ marginBottom: 12 }}>Tier ramp · S → F</div>
          <div style={{ display: 'flex', gap: 2, marginBottom: 14 }}>
            {[
              { t: 'S', c: '#00ff88' },
              { t: 'A', c: '#7bf06b' },
              { t: 'B', c: '#ffe14a' },
              { t: 'C', c: '#ffb84d' },
              { t: 'D', c: '#ff8a3c' },
              { t: 'E', c: '#ff5a3c' },
              { t: 'F', c: '#ff3355' },
            ].map((t) => (
              <div
                key={t.t}
                style={{
                  flex: 1,
                  height: 56,
                  background: t.c,
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 18,
                  fontWeight: 700,
                  color: '#0a0a0a',
                }}
              >
                {t.t}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 28 }}>
            {(['S', 'A', 'B', 'C', 'D', 'E', 'F'] as const).map((t) => (
              <TierBadge key={t} tier={t} size="md" label={false} />
            ))}
          </div>

          <div className="eyebrow" style={{ marginBottom: 12 }}>Speed ramp · 0 → top</div>
          <div
            style={{
              height: 36,
              borderRadius: 4,
              background: 'linear-gradient(to right, #1a4e8a, #0099ff, #00d4c4, #7bf06b, #ffe14a, #ff8a3c, #ff3355)',
              marginBottom: 8,
            }}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              color: 'var(--fg-muted)',
            }}
          >
            <span>0 km/h</span>
            <span>~120</span>
            <span>~240</span>
            <span>top</span>
          </div>
        </div>

        <div>
          <div className="eyebrow" style={{ marginBottom: 12 }}>Typography · IBM Plex Sans + IBM Plex Mono</div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 18,
              padding: 24,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-8)',
              marginBottom: 28,
            }}
          >
            <div>
              <div className="eyebrow">H1 · 48 / 1.05 · 600 · −0.02em</div>
              <div className="h1" style={{ marginTop: 4 }}>
                Inspect any replay's
                <br />
                input telemetry.
              </div>
            </div>
            <div>
              <div className="eyebrow">H2 · 28 / 1.15 · 600</div>
              <div className="h2" style={{ marginTop: 4 }}>Suspicious input clusters detected</div>
            </div>
            <div>
              <div className="eyebrow">H3 · 18 / 1.3 · 600</div>
              <div className="h3" style={{ marginTop: 4 }}>Steering input distribution</div>
            </div>
            <div>
              <div className="eyebrow">Body · 14 / 1.55 · 400 · fg-2</div>
              <p className="p" style={{ marginTop: 4 }}>
                We saw 412 identical steering inputs of value 34.7% clustered between 00:18.230 and 00:21.700. That's not how an
                analog stick behaves.
              </p>
            </div>
            <div>
              <div className="eyebrow">Mono numerics · tabular figures</div>
              <div className="mono" style={{ fontSize: 22, marginTop: 4, color: 'var(--fg-1)', fontWeight: 600 }}>
                00:56.812 &nbsp; 348 km/h &nbsp; 0.347 &nbsp; G4
              </div>
            </div>
            <div>
              <div className="eyebrow">Eyebrow · 11 · mono · uppercase · 0.12em</div>
              <div className="eyebrow" style={{ marginTop: 4 }}>Replay UID · a4f1-2c81-9e44</div>
            </div>
          </div>

          <div className="eyebrow" style={{ marginBottom: 12 }}>Replay overlay colors · Compare</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {[
              { c: '#0099ff', n: 'rep-1 · BLUE' },
              { c: '#ff6b35', n: 'rep-2 · ORANGE' },
              { c: '#b56bff', n: 'rep-3 · PURPLE' },
              { c: '#00ff88', n: 'rep-4 · LIME' },
            ].map((r) => (
              <div
                key={r.n}
                style={{
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  overflow: 'hidden',
                }}
              >
                <div style={{ height: 44, background: r.c }} />
                <div style={{ padding: 8, fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-3)' }}>{r.n}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ComponentsFrame({ replay, loading }: { replay: Replay | null; loading: boolean }) {
  return (
    <div className="tm" style={{ background: 'var(--bg-page)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 28 }}>
        <h1 className="h1" style={{ color: 'var(--fg-1)' }}>Component library</h1>
        <span className="eyebrow">Building blocks</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
        <section>
          <div className="eyebrow" style={{ marginBottom: 12 }}>Tier badge · 4 sizes</div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
              padding: 18,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-8)',
            }}
          >
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <TierBadge tier="S" size="xl" />
              <TierBadge tier="F" size="xl" />
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              {(['S', 'A', 'B', 'C', 'D', 'E', 'F'] as const).map((t) => (
                <TierBadge key={t} tier={t} size="lg" />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {(['S', 'A', 'B', 'C', 'D', 'E', 'F'] as const).map((t) => (
                <TierBadge key={t} tier={t} size="md" />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {(['S', 'A', 'B', 'C', 'D', 'E', 'F'] as const).map((t) => (
                <TierBadge key={t} tier={t} size="sm" label={false} />
              ))}
            </div>
          </div>
        </section>

        <section>
          <div className="eyebrow" style={{ marginBottom: 12 }}>Buttons & chips</div>
          <div
            style={{
              padding: 18,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-8)',
            }}
          >
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <Button variant="primary" icon={<Icon.Scan />}>Scan replay</Button>
              <Button variant="secondary" icon={<Icon.Compare />}>Compare</Button>
              <Button variant="ghost">Cancel</Button>
              <Button variant="danger" icon={<Icon.Warn />}>Reject</Button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <Button variant="primary" size="sm">Small</Button>
              <Button variant="secondary" size="lg" icon={<Icon.Upload />}>Upload .gbx</Button>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Chip active>All maps</Chip>
              <Chip icon={<Icon.Filter />}>Filter</Chip>
            </div>
          </div>
        </section>

        <section>
          <div className="eyebrow" style={{ marginBottom: 12 }}>Replay summary card</div>
          {replay ? (
            <div style={{ display: 'flex', gap: 12 }}>
              <ReplayCard replay={replay} />
            </div>
          ) : (
            <EmptyState
              title={loading ? 'Loading…' : 'No replays yet'}
              body="Scan a replay first to see this component populated."
              icon={<Icon.Scan />}
            />
          )}
        </section>

        <section>
          <div className="eyebrow" style={{ marginBottom: 12 }}>Chart frame</div>
          {replay ? (
            <SyncContext.Provider value={{ scrubT: 0.5, hoverT: null, setHoverT: () => {} }}>
              <SpeedChart replay={replay} height={110} />
            </SyncContext.Provider>
          ) : (
            <EmptyState
              title={loading ? 'Loading…' : 'No replays yet'}
              body="The chart frame needs tick data to render."
              icon={<Icon.Scan />}
            />
          )}
        </section>

        <section>
          <div className="eyebrow" style={{ marginBottom: 12 }}>States</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <EmptyState
              title="No replay loaded"
              body="Paste a replay URL or drop a .Replay.gbx file to begin."
              icon={<Icon.Upload />}
              cta="Choose file"
            />
            <ParseProgress
              steps={[
                { label: 'Reading GBX headers', done: true },
                { label: 'Extracting input ticks', done: true },
                { label: 'Detecting clusters', done: false, active: true },
                { label: 'Computing verdict', done: false },
              ]}
            />
          </div>
        </section>

        <section>
          <div className="eyebrow" style={{ marginBottom: 12 }}>Speed-graded racing line</div>
          {replay ? (
            <div
              style={{
                height: 200,
                background: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-8)',
                overflow: 'hidden',
              }}
            >
              <Minimap replay={replay} width={640} height={200} scrubT={null} showLabels={false} />
            </div>
          ) : (
            <EmptyState
              title={loading ? 'Loading…' : 'No replays yet'}
              body="The minimap component plots a real racing line."
              icon={<Icon.Scan />}
            />
          )}
        </section>
      </div>
    </div>
  );
}
